/**
 * Analysis chat client.
 *
 * Talks to /analysis/* on the api gateway, which proxies to the optimizer
 * service. See docs/plans/2026-05-01-analysis-chat-design.md (in optimizer
 * repo) for the full design.
 */
import { api } from './api'

// ---- Types -----------------------------------------------------------------

export type ChartType = 'bar' | 'line' | 'area' | 'pie'

export interface ChartSpec {
  chart_type: ChartType
  x: string
  y: string
  group?: string
  stacked?: boolean
  result_id?: string
  title?: string
}

export type Segment =
  | { type: 'text'; text: string }
  | { type: 'markdown'; text: string }
  | { type: 'tool_call'; id: string; name: string; args: Record<string, unknown> }
  | {
      type: 'tool_result'
      id: string
      rows: Record<string, unknown>[]
      columns: string[]
      truncated?: boolean
      error?: boolean
    }
  | { type: 'chart_spec'; result_id: string; spec: ChartSpec }

export interface AnalysisSummary {
  id: number
  name: string
  shared: boolean
  archived: boolean
  user_id: number
  last_message_at: string
  total_input_tokens: number
  total_output_tokens: number
  total_cost_usd: number
  created_at: string
  updated_at: string
}

export interface AnalysisMessage {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: Segment[]
  provider?: string | null
  model?: string | null
  input_tokens?: number | null
  output_tokens?: number | null
  cost_usd?: number | null
  created_at: string
}

export interface AnalysisDetail extends AnalysisSummary {
  messages: AnalysisMessage[]
}

// ---- Stream events (SSE) ---------------------------------------------------

export type StreamEvent =
  | { type: 'token'; delta: string }
  | { type: 'tool_call'; id: string; name: string; args: Record<string, unknown> }
  | {
      type: 'tool_result'
      id: string
      rows_count: number
      columns: string[]
      truncated?: boolean
      error?: boolean
    }
  | { type: 'error'; code: string; message: string }
  | {
      type: 'done'
      message_id: number
      tokens: { input: number; output: number }
      cost_usd: number
      provider?: string
      model?: string
    }

// ---- CRUD calls ------------------------------------------------------------

export async function listAnalyses(includeArchived = false): Promise<AnalysisSummary[]> {
  const res = await api.get('/analysis/', {
    params: includeArchived ? { include_archived: true } : undefined,
  })
  return res.data
}

export async function createAnalysis(name?: string): Promise<AnalysisSummary> {
  const res = await api.post('/analysis/', { name: name ?? null })
  return res.data
}

export async function getAnalysis(id: number): Promise<AnalysisDetail> {
  const res = await api.get(`/analysis/${id}`)
  return res.data
}

export async function patchAnalysis(
  id: number,
  patch: { name?: string; shared?: boolean; archived?: boolean }
): Promise<AnalysisSummary> {
  const res = await api.patch(`/analysis/${id}`, patch)
  return res.data
}

export async function deleteAnalysis(id: number): Promise<void> {
  await api.delete(`/analysis/${id}`)
}

export async function submitMessage(id: number, text: string): Promise<void> {
  await api.post(`/analysis/${id}/messages`, { text })
}

// ---- SSE streaming ---------------------------------------------------------

/**
 * Open an SSE stream for an in-progress analysis turn.
 *
 * Uses fetch + ReadableStream (not EventSource) for two reasons:
 *   1. EventSource cannot send custom headers — and the api gateway requires
 *      `Authorization: Bearer <jwt>` from localStorage.
 *   2. fetch + AbortController gives us a clean way to cancel an in-progress
 *      turn from the UI (Stop button).
 *
 * Returns an abort function. Calling it cancels the network request; the
 * server-side orchestrator continues to completion (its events are dropped),
 * and the next page load will pick up the persisted assistant message.
 */
export function streamTurn(
  analysisId: number,
  onEvent: (event: StreamEvent) => void
): () => void {
  const baseURL = (api.defaults.baseURL || '').replace(/\/$/, '')
  const url = `${baseURL}/analysis/${analysisId}/stream`

  // Mirror the axios interceptor's auth + org headers.
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
  }
  const authToken = localStorage.getItem('authToken')
  if (authToken) {
    headers.Authorization = authToken.startsWith('Bearer ')
      ? authToken
      : `Bearer ${authToken}`
  }
  const orgSlug = getOrgSlugFromUrl() || localStorage.getItem('activeOrganization')
  if (orgSlug) headers['X-Organization-Slug'] = orgSlug

  const controller = new AbortController()
  let aborted = false

  void (async () => {
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: controller.signal,
      })
      if (!res.ok || !res.body) {
        const body = await res.text().catch(() => '')
        onEvent({
          type: 'error',
          code: `http_${res.status}`,
          message: body || `HTTP ${res.status}`,
        })
        onEvent({
          type: 'done',
          message_id: 0,
          tokens: { input: 0, output: 0 },
          cost_usd: 0,
        })
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        // SSE events are separated by a blank line.
        let sepIdx
        while ((sepIdx = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, sepIdx)
          buf = buf.slice(sepIdx + 2)
          dispatchSseEvent(raw, onEvent)
        }
      }
      // Drain any final partial event (rare).
      if (buf.trim()) dispatchSseEvent(buf, onEvent)
    } catch (err) {
      if (aborted || (err as Error)?.name === 'AbortError') {
        // User-initiated abort or page navigation. Surface a clean done so
        // the UI can unstick its "thinking" indicator.
        onEvent({
          type: 'done',
          message_id: 0,
          tokens: { input: 0, output: 0 },
          cost_usd: 0,
        })
        return
      }
      onEvent({
        type: 'error',
        code: 'network',
        message: (err as Error)?.message || 'Network error',
      })
      onEvent({
        type: 'done',
        message_id: 0,
        tokens: { input: 0, output: 0 },
        cost_usd: 0,
      })
    }
  })()

  return () => {
    aborted = true
    controller.abort()
  }
}

function getOrgSlugFromUrl(): string | null {
  const parts = window.location.pathname.substring(1).split('/')
  return parts[0] || null
}

function dispatchSseEvent(raw: string, onEvent: (e: StreamEvent) => void): void {
  let eventType = 'message'
  let dataStr = ''
  for (const line of raw.split('\n')) {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim()
    } else if (line.startsWith('data: ')) {
      // Multi-line data lines are concatenated with newlines (SSE spec).
      dataStr += (dataStr ? '\n' : '') + line.slice(6)
    }
  }
  if (!dataStr) return
  try {
    const data = JSON.parse(dataStr)
    onEvent({ type: eventType, ...data } as StreamEvent)
  } catch (err) {
    console.warn(`SSE payload not JSON for event '${eventType}':`, dataStr, err)
  }
}
